/**
 * Air times and their presentation.
 *
 * Simkl's `/tv/episodes/{id}?extended=full` returns a `date` that is already a
 * full ISO 8601 instant with the network's UTC offset baked in:
 *
 *   "2026-09-17T03:00:00-04:00"
 *
 * That removes the whole timezone-guessing problem the plan anticipated. Parse
 * it to a UTC instant once at ingest, format it in the device's zone at render,
 * and daylight-saving handling is the platform's job rather than ours.
 *
 * The documented `/calendar/*.json` paths all 404; this is the working source.
 * Only shows with unaired episodes need fetching — eight, not eighty.
 */
import { formatCode } from "@/lib/episode";

export interface Episode {
  season: number;
  episode: number;
  title: string;
  /** Null when Simkl has no date for the episode at all. */
  airsAt: Date | null;
  aired: boolean;
  image: string | null;
}

export interface UpcomingEpisode extends Episode {
  showId: number;
  showTitle: string;
  showPoster: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Parses one episode from the live `/tv/episodes/{id}` shape. */
export function parseEpisode(raw: unknown): Episode | null {
  if (!isRecord(raw)) return null;
  const season = raw["season"];
  const episode = raw["episode"];
  if (typeof season !== "number" || typeof episode !== "number") return null;

  const date = raw["date"];
  let airsAt: Date | null = null;
  if (typeof date === "string") {
    const parsed = new Date(date);
    if (!Number.isNaN(parsed.getTime())) airsAt = parsed;
  }
  const title = raw["title"];
  const img = raw["img"];
  return {
    season,
    episode,
    title: typeof title === "string" ? title : "",
    airsAt,
    aired: raw["aired"] === true,
    image: typeof img === "string" && img.length > 0 ? img : null,
  };
}

export function parseEpisodes(raw: unknown): Episode[] {
  if (!Array.isArray(raw)) return [];
  const episodes: Episode[] = [];
  for (const entry of raw) {
    const parsed = parseEpisode(entry);
    if (parsed !== null) episodes.push(parsed);
  }
  return episodes;
}

const DAY_MS = 86_400_000;

/** Calendar days between two instants, in the device's zone — not elapsed hours. */
export function daysUntil(target: Date, from: Date): number {
  const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const b = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  return Math.round((a.getTime() - b.getTime()) / DAY_MS);
}

/**
 * How far away, always precisely.
 *
 * Calendar-day based, so an episode at 23:00 tonight reads "Today" rather than
 * "in 3 hours", and one at 01:00 tomorrow reads "Tomorrow" rather than
 * "in 5 hours".
 *
 * Never returns a bucket like "Next week": two groups eleven days apart both
 * landing on the same phrase reads as a bug, and it was one.
 */
export function relativeDay(target: Date, from: Date = new Date()): string {
  const days = daysUntil(target, from);
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";
  if (days < 0) return `${Math.abs(days)} days ago`;
  return `In ${days} days`;
}

/**
 * Day heading. One shape for every row past tomorrow, so adjacent headings
 * never differ in form — "Monday" above "Thursday, 24 Sep" reads as an
 * inconsistency even though both are correct.
 */
export function dayLabel(target: Date, from: Date = new Date()): string {
  const days = daysUntil(target, from);
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return target.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
}

export function timeLabel(target: Date): string {
  return target.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export interface DayGroup {
  key: string;
  date: Date;
  episodes: UpcomingEpisode[];
}

/** Groups upcoming episodes by local calendar day, soonest first. */
export function groupByDay(episodes: readonly UpcomingEpisode[]): DayGroup[] {
  const groups = new Map<string, DayGroup>();
  for (const episode of episodes) {
    if (episode.airsAt === null) continue;
    const date = episode.airsAt;
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    const group = groups.get(key) ?? {
      key,
      date: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
      episodes: [],
    };
    group.episodes.push(episode);
    groups.set(key, group);
  }
  return [...groups.values()]
    .toSorted((x, y) => x.date.getTime() - y.date.getTime())
    .map((group) => ({
      ...group,
      episodes: group.episodes.toSorted(
        (x, y) => (x.airsAt?.getTime() ?? 0) - (y.airsAt?.getTime() ?? 0),
      ),
    }));
}

/** The episode code, from the one formatter every screen shares. */
export function codeOf(episode: Episode): string {
  return formatCode({ season: episode.season, episode: episode.episode });
}

/** Compact air date for the episode drawer, e.g. "12 Mar 24". */
export function shortDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
}
